/**
 * Timezone reporter for the unified web dashboard
 * (`app.tryhero.io`).
 *
 * The AI's outreach layer needs the user's local timezone to honour
 * DND windows and business hours. Reads it from
 * `Intl.DateTimeFormat().resolvedOptions().timeZone` and syncs to
 * `User.meta.timezone` via `PATCH /current-user/meta`.
 *
 * ### What this hook is NOT
 *
 * This is **not** a `lastSeenAt` heartbeat. Since the SDK attaches
 * `x-hero-app: 'dashboard'` + `x-hero-platform: 'web'` on every
 * outbound request, the backend `PresenceInterceptor` already bumps
 * `User.meta.presence.apps[('dashboard', 'web')].lastSeenAt` on
 * every authed request (throttled to once per 5 minutes per user).
 * The dashboard doesn't have OS-level `appVersion` / `osVersion` /
 * permissions to report either — those are mobile-only signals. So
 * this hook is a small side-channel: on login, and once per focus
 * event throttle window, push the timezone if it looks stale or
 * missing.
 *
 * ### Why the gate is `useIsLoggedIn()` + user, not just user
 *
 * `useCurrentUser().isLoggedIn` is derived from the
 * `useCurrentUserApi()` query, which has `persist: true`
 * (`ENTITY_CONFIGS.currentUser`). On a hard reload, the persisted
 * cache resolves synchronously and reports `isLoggedIn === true`
 * **before** the RAM access token has been rehydrated by
 * `useAuthBootstrap`'s silent-refresh. Firing the PATCH in that
 * window would ship a bearer-less request and eat a 401. We gate
 * on `useIsLoggedIn()` (the reactive read of the RAM token) so the
 * effect only fires once auth is genuinely ready — and re-fires
 * automatically when the silent refresh completes.
 *
 * Errors are swallowed on purpose — a failed timezone sync must
 * never take a dashboard tab offline. The next focus event will
 * retry. See `docs/ai/user-signals.md`.
 */
import { useEffect, useRef } from 'react';
import { useUpdateCurrentUserMetaApi } from '@hero/api-hooks';
import type { CurrentUserMetaUpdateAPI } from '@hero/api';
import { useIsLoggedIn } from '../auth/useAuthBootstrap';

const SYNC_THROTTLE_MS = 5 * 60 * 1000;

function getTimezone(): string | undefined {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
  } catch {
    return undefined;
  }
}

export function usePresenceHeartbeatManager(): void {
  // Reactive read of the RAM access token — flips true only after
  // `useAuthBootstrap` finishes its silent refresh on cold-boot.
  // This is the ground truth for "we can hit an authed endpoint
  // right now"; the persisted `currentUser` cache alone is not.
  const isAuthed = useIsLoggedIn();
  const { mutate } = useUpdateCurrentUserMetaApi({
    onError: () => {
      // Intentional no-op — see the header comment.
    },
  });

  const lastSentAtRef = useRef<number>(0);

  useEffect(() => {
    if (!isAuthed) {
      // Reset the throttle so the next login fires a fresh sync
      // immediately.
      lastSentAtRef.current = 0;
      return;
    }

    const send = (): void => {
      const now = Date.now();
      if (now - lastSentAtRef.current < SYNC_THROTTLE_MS) return;
      const timezone = getTimezone();
      // Nothing to say — skip the write, don't waste the throttle
      // slot on an empty PATCH.
      if (!timezone) return;
      lastSentAtRef.current = now;

      const payload: CurrentUserMetaUpdateAPI['req'] = { timezone };
      mutate(payload);
    };

    send();

    const onFocus = (): void => send();
    window.addEventListener('focus', onFocus);

    const onVisibility = (): void => {
      if (document.visibilityState === 'visible') send();
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [isAuthed, mutate]);
}
